import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientAPI } from '@/shared/lib/api';
import { Search, Heart, Package, Plus, X, TrendingUp, ExternalLink } from 'lucide-react';
import { Card, CardContent, Spinner } from '@travellegacy/nexops-design-system';

interface LoyaltyProgram {
  pointsPerUnit: number;
  pointsMax: number | null;
  isUpTo: boolean;
  currency: string;
  clubPointsPerUnit: number | null;
  promotion: boolean;
  previousPointsPerUnit: number | null;
  ruleText: string;
  lastCollectedAt: string;
  url: string | null;
  targetUrl?: string | null;
}

interface LoyaltyPartner {
  id: string;
  slug: string;
  name: string;
  logoUrl: string | null;
  categories: string[];
  inLivelo: boolean;
  inEsfera: boolean;
  livelo: LoyaltyProgram | null;
  esfera: LoyaltyProgram | null;
}

interface FavoritePartner {
  id: string;
  userId: string;
  partnerId: string;
  createdAt: string;
  partner: LoyaltyPartner;
}

export function FavoritePartnersSection() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch favorites (sempre)
  const { data: favoritesData, isLoading: isLoadingFavorites } = useQuery({
    queryKey: ['favorite-partners'],
    queryFn: async () => {
      const response = await clientAPI.getFavoritePartners();
      return response.data.data as FavoritePartner[];
    },
  });

  // Fetch all partners (somente quando o modal abre)
  const { data: partnersData, isLoading: isLoadingPartners } = useQuery({
    queryKey: ['loyalty-partners'],
    queryFn: async () => {
      const response = await clientAPI.getLoyaltyPartners();
      return response.data.data as { livelo: LoyaltyPartner[]; esfera: LoyaltyPartner[] };
    },
    enabled: isModalOpen,
  });

  const favorites = useMemo(() => favoritesData || [], [favoritesData]);

  const favoriteIds = useMemo(() => {
    return new Set(favorites.map((f) => f.partnerId));
  }, [favorites]);

  // Merge de parceiros (Livelo + Esfera, sem duplicados)
  const allPartners = useMemo(() => {
    if (!partnersData) return [];
    const partnersMap = new Map<string, LoyaltyPartner>();
    partnersData.livelo.forEach((p) => partnersMap.set(p.id, p));
    partnersData.esfera.forEach((p) => {
      if (!partnersMap.has(p.id)) {
        partnersMap.set(p.id, p);
      }
    });
    return Array.from(partnersMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name, 'pt-BR')
    );
  }, [partnersData]);

  // Filtro de busca no modal
  const filteredPartners = useMemo(() => {
    if (!searchQuery) return allPartners;
    const query = searchQuery.toLowerCase();
    return allPartners.filter((p) => p.name.toLowerCase().includes(query));
  }, [allPartners, searchQuery]);

  // Mutations
  const addFavoriteMutation = useMutation({
    mutationFn: async (partnerId: string) => {
      await clientAPI.addFavoritePartner(partnerId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorite-partners'] });
    },
  });

  const removeFavoriteMutation = useMutation({
    mutationFn: async (partnerId: string) => {
      await clientAPI.removeFavoritePartner(partnerId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorite-partners'] });
    },
  });

  const toggleFavorite = (partnerId: string) => {
    if (favoriteIds.has(partnerId)) {
      removeFavoriteMutation.mutate(partnerId);
    } else {
      addFavoriteMutation.mutate(partnerId);
    }
  };

  const isMutating = addFavoriteMutation.isPending || removeFavoriteMutation.isPending;

  const closeModal = () => {
    setIsModalOpen(false);
    setSearchQuery('');
  };

  return (
    <div className="space-y-4">
      {/* Header da Seção */}
      <h2 className="text-2xl font-bold text-foreground">Lojas Favoritas</h2>

      {/* Conteúdo */}
      {isLoadingFavorites ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : favorites.length === 0 ? (
        <Card>
          <CardContent className="p-0">
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="p-4 bg-secondary/50 rounded-full mb-4">
                <Heart className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-1">
                Nenhuma loja favorita
              </h3>
              <p className="text-sm text-muted-foreground max-w-md mb-4">
                Adicione suas lojas preferidas para acompanhar as pontuações da Livelo e
                Esfera direto na dashboard.
              </p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm bg-primary hover:bg-primary/90 text-primary-foreground transition-all"
              >
                <Plus className="h-4 w-4" />
                <span>Adicionar Lojas</span>
              </button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {favorites.map((favorite) => {
            const partner = favorite.partner;
            const hasPromotion = partner.livelo?.promotion || partner.esfera?.promotion;

            return (
              <div
                key={favorite.id}
                className="relative rounded-xl p-5 transition-all hover:shadow-lg flex flex-col bg-card border border-border hover:border-primary/50"
              >
                {/* Botão remover favorito */}
                <button
                  onClick={() => removeFavoriteMutation.mutate(partner.id)}
                  disabled={isMutating}
                  title="Remover das favoritas"
                  className="absolute top-3 right-3 p-1.5 rounded-full text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors"
                >
                  <Heart className="h-4 w-4 fill-current" />
                </button>

                {/* Logo e Nome */}
                <div className="flex items-start gap-3 mb-4 pr-8">
                  <div className="flex-shrink-0">
                    {partner.logoUrl ? (
                      <img
                        src={partner.logoUrl}
                        alt={partner.name}
                        className="h-12 w-12 object-contain rounded-lg bg-white p-1 border border-border"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Package className="h-6 w-6 text-primary" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-foreground truncate leading-tight">
                      {partner.name}
                    </h3>
                    {hasPromotion && (
                      <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 text-xs font-bold rounded-md bg-gradient-to-r from-amber-500 to-orange-500 text-white">
                        <TrendingUp className="h-3 w-3" />
                        Promoção
                      </span>
                    )}
                  </div>
                </div>

                {/* Pontuações */}
                <div className="space-y-2 mb-auto">
                  {partner.inLivelo && partner.livelo && (
                    <div className="flex items-center justify-between p-2.5 bg-gradient-to-r from-fuchsia-50 to-fuchsia-100 dark:from-fuchsia-950/30 dark:to-fuchsia-900/20 rounded-lg border border-fuchsia-200 dark:border-fuchsia-800">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-fuchsia-500 rounded-full" />
                        <span className="text-xs font-semibold text-fuchsia-900 dark:text-fuchsia-100">
                          Livelo
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="text-right">
                          <span className="text-sm font-bold text-fuchsia-900 dark:text-fuchsia-100">
                            {partner.livelo.isUpTo ? 'até ' : ''}
                            {partner.livelo.pointsPerUnit} pts
                          </span>
                          <span className="text-xs text-fuchsia-700 dark:text-fuchsia-300 ml-1">
                            / {partner.livelo.currency === 'BRL' ? 'R$' : partner.livelo.currency}
                          </span>
                        </div>
                        {partner.livelo.url && (
                          <a
                            href={partner.livelo.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Ver loja na Livelo"
                            className="flex items-center gap-1 px-1.5 py-1 rounded-md text-xs font-semibold text-fuchsia-600 dark:text-fuchsia-300 hover:bg-fuchsia-200/70 dark:hover:bg-fuchsia-900/40 transition-colors"
                          >
                            <span>Abrir</span>
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {partner.inEsfera && partner.esfera && (
                    <div className="flex items-center justify-between p-2.5 bg-gradient-to-r from-red-50 to-red-100 dark:from-red-950/30 dark:to-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full" />
                        <span className="text-xs font-semibold text-red-900 dark:text-red-100">
                          Esfera
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="text-right">
                          <span className="text-sm font-bold text-red-900 dark:text-red-100">
                            {partner.esfera.isUpTo ? 'até ' : ''}
                            {partner.esfera.pointsPerUnit} pts
                          </span>
                          <span className="text-xs text-red-700 dark:text-red-300 ml-1">
                            / {partner.esfera.currency === 'BRL' ? 'R$' : partner.esfera.currency}
                          </span>
                        </div>
                        {partner.esfera.url && (
                          <a
                            href={partner.esfera.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Ver loja na Esfera"
                            className="flex items-center gap-1 px-1.5 py-1 rounded-md text-xs font-semibold text-red-600 dark:text-red-300 hover:bg-red-200/70 dark:hover:bg-red-900/40 transition-colors"
                          >
                            <span>Abrir</span>
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Card para adicionar mais lojas */}
          <button
            onClick={() => setIsModalOpen(true)}
            className="rounded-xl p-5 flex flex-col items-center justify-center gap-3 min-h-[160px] border-2 border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary hover:bg-primary/5 transition-all"
          >
            <div className="p-3 rounded-full bg-secondary">
              <Plus className="h-6 w-6" />
            </div>
            <span className="text-sm font-semibold">Adicionar Lojas</span>
          </button>
        </div>
      )}

      {/* Modal de Seleção de Lojas */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={closeModal}
        >
          <div
            className="w-full max-w-lg bg-card border border-border rounded-xl shadow-2xl flex flex-col max-h-[80vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header do Modal */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h3 className="text-lg font-bold text-foreground">Adicionar Lojas Favoritas</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {favoriteIds.size} {favoriteIds.size === 1 ? 'loja favorita' : 'lojas favoritas'}
                </p>
              </div>
              <button
                onClick={closeModal}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Busca */}
            <div className="p-4 border-b border-border">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  autoFocus
                  placeholder="Buscar lojas..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-secondary/50 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-all"
                />
              </div>
            </div>

            {/* Lista de Lojas */}
            <div className="flex-1 overflow-y-auto p-2">
              {isLoadingPartners ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner size="md" />
                </div>
              ) : filteredPartners.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Search className="h-8 w-8 text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">Nenhuma loja encontrada</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredPartners.map((partner) => {
                    const isFavorite = favoriteIds.has(partner.id);

                    return (
                      <button
                        key={partner.id}
                        onClick={() => toggleFavorite(partner.id)}
                        disabled={isMutating}
                        className={`w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-colors ${
                          isFavorite
                            ? 'bg-rose-50 dark:bg-rose-950/20 hover:bg-rose-100 dark:hover:bg-rose-950/30'
                            : 'hover:bg-secondary'
                        }`}
                      >
                        {/* Logo */}
                        {partner.logoUrl ? (
                          <img
                            src={partner.logoUrl}
                            alt={partner.name}
                            className="h-9 w-9 object-contain rounded-md bg-white p-0.5 border border-border flex-shrink-0"
                          />
                        ) : (
                          <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Package className="h-4 w-4 text-primary" />
                          </div>
                        )}

                        {/* Nome + Programas */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground truncate">
                            {partner.name}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {partner.inLivelo && (
                              <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                                Livelo
                              </span>
                            )}
                            {partner.inEsfera && (
                              <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                                Esfera
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Heart */}
                        <Heart
                          className={`h-5 w-5 flex-shrink-0 transition-colors ${
                            isFavorite
                              ? 'text-rose-500 fill-current'
                              : 'text-muted-foreground'
                          }`}
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-border">
              <button
                onClick={closeModal}
                className="w-full px-4 py-2.5 rounded-lg font-semibold text-sm bg-primary hover:bg-primary/90 text-primary-foreground transition-all"
              >
                Concluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
